import Immutable, { List, Record } from 'immutable';
import guid from '../../../../libraries/utils/guid';
import unixTimestamp from '../../../../libraries/utils/unix-timestamp';
import ParseError from '../../../utils/helpers/errors/parse-error';
import ErrorsAggregatorError from '../../../utils/helpers/errors/errors-aggregator';
import { parse } from './message-entities/entities-factory';
import { MENTION, TEXT, TITLE } from './message-entities/types';
import { Status, PUBLISH_AND_MODERATE_WORDS, REJECTED_WORDS, RESTRICTED_WORDS, INVALID_CHARACTERS } from "./moderation";

var MessageTypes = {
  COMMENT: 'comment',
  REPLY: 'reply'
};

var Appearance = {
  NORMAL: 'normal',
  COLLAPSED: 'collapsed',
  APPROVED: 'approved',
  BLOCKED: 'blocked'
};

var MessageFields = Record({
  id: guid(), // Message's id
  parentId: null,
  status: Status.OTHER,
  clientAppearance: Appearance.NORMAL,
  conversationId: null, // Id of the conversation this message belong to
  conversationTitle: null,
  messageUrl: null,
  conversationMessageId: null,
  postedAt: new Date(), // Date of message post
  type: MessageTypes.COMMENT, // Type of this message, can be one from types enum
  frameType: '',
  userId: null, // Id of the sender user
  entities: List(), // Array of message entities (all entities including mentions)
  textEntities: List(), // Only text entities: used for accessing text entities only (all text entities are included also in entities)
  mentions: List(), // Only mentions
  ip: null,
  reportsCount: 0,
  tags: [],
  tagsData: [],
  highlightedTagSpans: {},
  published: false,
  isManuallyResolved: false,
  isEdited: false,
  userDisplayName: null,
  isAnonymous: false,
  expirationTime: '',
  highlightedWords: [],
  hashtags: [],
  isProcessing: false,
  safetyScore: undefined
});

class Message extends MessageFields {
  constructor(fields) {
    super(fields);
    this.checked = false;
  }
  isChecked() {
    return this.checked;
  }
  setChecked(value) {
    this.checked = value;
  }
  getText() {
    var text = null;
    var textEntities = this.get('textEntities');
    if (textEntities.count() > 0) {
      text = textEntities.first().get('text');
    }
    return text;
  }
  parse(data, expirationTime = '') {
    if (!data) {
      return this;
    }

    var fields = {};

    [
      'id',
      'status',
      'conversationId',
      'conversationTitle',
      'messageUrl',
      'type',
      'userId',
      'isManuallyResolved',
      'isAnonymous',
      'conversationMessageId',
      'ip',
      'reportsCount',
      'tagsData',
      'highlightedTagSpans',
      'parentId',
      'published',
      'frameType',
      'isEdited',
      'userDisplayName',
      'highlightedWords',
      'hashtags',
      'isProcessing',
      'safetyScore'
    ].forEach(field => {
      fields[field] =
        data[field] !== undefined && data[field] !== null ? data[field] : this.get(field);
    });

    fields.postedAt = data.postedAt ? unixTimestamp.toDate(data.postedAt) : this.get('postedAt');
    var jumpToMessage =
      (data.type === MessageTypes.COMMENT ? 'spot_im_comment_id=' : 'spot_im_reply_id=') +
      data.conversationMessageId;

    var queryIndex = -1;
    if (fields.messageUrl) {
      queryIndex = fields.messageUrl.indexOf('?');
      fields.messageUrl += queryIndex > -1 ? '&' + jumpToMessage : '?' + jumpToMessage;
    }

    var entities = null;
    var textEntities = null;
    var mentions = null;
    var tags = [];
    if (data.tags) {
      data.tags.forEach(tag => {
        if (tags.indexOf(tag) === -1) {
          tags.push(tag);
        }
      });
    } else {
      tags = this.get('tags');
    }

    if (data.entities) {
      var entitiesArr = [];
      var textEntitiesArr = [];
      var mentionsArr = [];
      var aggregator = new ErrorsAggregatorError('entitiesFactory');
      data.entities.forEach(entityData => {
        try {
          var entity = parse(entityData.type, entityData);
          if (entity) {
            entitiesArr.push(entity);
            if (entity.type === MENTION) {
              mentionsArr.push(entity);
            }
            if (entity.type === TEXT || entity.type === TITLE) {
              textEntitiesArr.push(entity);
            }
          }
        } catch (e) {
          var parseError = new ParseError('entitiesFactory', e.message);
          aggregator.addError(parseError);
        }

        if (aggregator.hasErrors()) {
          // Report error
        }
      });
      entities = List(entitiesArr);
      textEntities = List(textEntitiesArr);
      mentions = List(mentionsArr);
    } else {
      entities = this.get('entities');
      textEntities = this.get('textEntities');
      mentions = this.get('mentions');
    }
    // override entities
    fields['tags'] = tags;
    fields['entities'] = entities;
    fields['textEntities'] = textEntities;
    fields['mentions'] = mentions;
    fields['expirationTime'] = expirationTime;

    var newMessage = new Message(fields, this.clientId);
    return Immutable.is(this, newMessage) ? this : newMessage;
  }
  toJSON() {
    return {
      id: this.get('id'),
      type: this.get('type'),
      status: this.get('status'),
      conversationId: this.get('conversationId'),
      conversationMessageId: this.get('conversationMessageId'),
      conversationTitle: this.get('conversationTitle'),
      messageUrl: this.get('messageUrl'),
      postedAt: unixTimestamp.fromDate(this.get('postedAt')),
      userId: this.get('userId'),
      ip: this.get('ip'),
      reportsCount: this.get('reportsCount'),
      isManuallyResolved: this.isManuallyResolved,
      tags: this.get('tags'),
      tagsData: this.get('tagsData'),
      highlightedTagSpans: this.get('highlightedTagSpans'),
      parentId: this.get('parentId'),
      userDisplayName: this.userDisplayName,
      isAnonymous: this.isAnonymous,
      highlightedWords: this.get('highlightedWords'),
      entities: this.get('entities').map(function (entity) {
        return entity.toJSON();
      }),
      isProcessing: this.get('isProcessing'),
      safetyScore: this.get('safetyScore'),
      published: this.get('published')
    };
  }
  getRestrictedWords() {
    return (this.get('tagsData')[RESTRICTED_WORDS] || [])
      .concat(this.get('tagsData')[REJECTED_WORDS] || [])
      .concat(this.get('tagsData')[PUBLISH_AND_MODERATE_WORDS] || [])
      .concat(this.get('tagsData')[INVALID_CHARACTERS] || [])
      .concat(this.get('tagsData')['doxxing_words'] || [])
      .filter((item, pos, self) => {
        return self.indexOf(item) === pos;
      });
  }
  static fromJSON(json) {
    return new Message(null).parse(json);
  }
}

export { Message, MessageTypes, Appearance };
